import React, { useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { AI_SEARCH_POLL_INTERVAL, PAGE_SIZE } from 'utils/constants';
import { DjangoListResponse, AIConversation } from 'v2/models';
import InfiniteScroll from 'v2/common/InfiniteScroll/InfiniteScroll';
import restFactory from 'v2/utils/restFactory';
import NoAssociationsPlaceholder from 'v2/common/DRM/NoAssociationsPlaceholder/NoAssociationsPlaceholder';
import AISearchDetail from 'v2/pages/DrmFeatures/AISearch/AISearchDetail';
import CreateAISearchModal from 'v2/pages/DrmFeatures/AISearch/CreateAISearchModal/CreateAISearchModal';
import styles from 'v2/pages/DrmFeatures/AISearch/aiSearch.module.scss';
import wandIcon from 'v2/icons/wandIcon.svg';
import AISearchItem from 'v2/pages/DrmFeatures/AISearch/AISearchItem/AISearchItem';
import {
  CurrentStateWaitingConversationContext,
  getWaitingConversation,
  isAiSearchRunning,
} from 'v2/utils/AISearchUtils';
import useQueryParams from 'v2/hooks/useQueryParams';
import { aiConversationKeys } from 'v2/queries';

const AISearchContainer = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hasNameChanged, setHasNameChanged] = useState(false);
  const [hasNewSearchCreated, setHasNewSearchCreated] = useState(false);
  const [waitingConversation, setWaitingConversation] = useState<
    number | undefined
  >(undefined);
  const [selectedConversation, setSelectedConversation] = useState<
    AIConversation | undefined
  >();
  const queryParams = useQueryParams();

  const handleCloseCreateAISearchModal = (hasNewSearchCreated?: boolean) => {
    setHasNewSearchCreated(Boolean(hasNewSearchCreated));
    setShowCreateModal(false);
    refetch();
  };

  const { data, isFetching, isLoading, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery(
      ['AIConversationList'],
      async ({ pageParam = 1 }) => {
        const res = await restFactory.get<DjangoListResponse<AIConversation>>(
          `/dj/api/v1/ai_conversation/`,
          {
            page: pageParam,
            size: PAGE_SIZE,
          }
        );

        return {
          ...res,
          prev: pageParam,
          next: pageParam + 1,
        };
      },
      {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => {
          const hasMorePages = PAGE_SIZE * lastPage.prev < lastPage.count;
          return hasMorePages ? lastPage.next : undefined;
        },
        onSuccess: (data) => {
          const searchId =
            selectedConversation?.id ?? Number(queryParams.get('search'));
          const selected = data?.pages
            .flatMap((page) => page.results)
            .find((item) => item.id === searchId);
          setSelectedConversation(selected);

          queryClient.invalidateQueries(aiConversationKeys.detail(searchId));
        },
        refetchInterval: (data) => {
          const isRunning = data?.pages
            .flatMap((page) => page.results)
            .some((item) => isAiSearchRunning(item));
          return isRunning ? AI_SEARCH_POLL_INTERVAL : false;
        },
      }
    );

  const conversations = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data]
  );

  const shouldSelectAutomatically =
    !selectedConversation ||
    hasNewSearchCreated ||
    hasNameChanged ||
    !conversations.find(
      (conversation) => conversation.id === selectedConversation.id
    );

  useEffect(() => {
    if (shouldSelectAutomatically) {
      setSelectedConversation(conversations?.[0]);
      setHasNameChanged(false);
      setHasNewSearchCreated(false);
      window.scrollTo(0, 0);
    }
  }, [conversations]);

  useEffect(() => {
    setWaitingConversation(getWaitingConversation(conversations));
  }, [conversations, selectedConversation]);

  const noConversations = !data || data.pages[0]?.count === 0;

  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const lastWeek = moment().subtract(7, 'days').startOf('day');

  const conversationFilters = {
    Today: (conversation: AIConversation) =>
      moment(conversation.updated_at) >= today,
    Yesterday: (conversation: AIConversation) =>
      moment(conversation.updated_at) >= yesterday &&
      moment(conversation.updated_at) < today,
    'Previous 7 days': (conversation: AIConversation) =>
      moment(conversation.updated_at) >= lastWeek &&
      moment(conversation.updated_at) < yesterday,
    Previous: (conversation: AIConversation) =>
      moment(conversation.updated_at) < lastWeek,
  };

  const handleChangeOfName = (isChange: boolean) => {
    setHasNameChanged(isChange);
  };

  return (
    <div className={classnames(styles.container)}>
      {isLoading ? (
        <div className="loadingIndicatorContainer">
          <Spinner animation="border" />
        </div>
      ) : noConversations ? (
        <NoAssociationsPlaceholder
          onButtonClick={() => setShowCreateModal(true)}
          buttonLabel="Create a new search"
          image={wandIcon}
          message={`You haven't created any searches yet.\nClick below to create one now.`}
          fitContentBtn
        />
      ) : (
        <>
          <CurrentStateWaitingConversationContext.Provider
            value={{
              waitingConversation,
            }}
          >
            <div
              className={styles.leftPanel}
              id="ai-conversation-list-container"
            >
              <div className={styles.newSearchButton}>
                <Button
                  variant="secondary"
                  className="btn-secondary-dark"
                  onClick={() => setShowCreateModal(true)}
                >
                  New Search
                </Button>
              </div>
              <div className={styles.searches}>
                {Object.entries(conversationFilters).map(([title, filter]) => {
                  const conversationGroup = conversations.filter(filter);
                  if (conversationGroup.length === 0) return null;
                  return (
                    <React.Fragment key={title}>
                      <h6>{title}</h6>
                      {conversationGroup.map((conversation) => {
                        const isSelected =
                          conversation.id === selectedConversation?.id;

                        return (
                          <Button
                            onClick={() =>
                              setSelectedConversation(conversation)
                            }
                            key={conversation.id}
                            aria-selected={isSelected}
                            onKeyUp={(e) => {
                              if (e.key === ' ') {
                                e.preventDefault();
                              }
                            }}
                          >
                            <AISearchItem
                              refetchConversations={refetch}
                              aiConversation={conversation}
                              handleChangeOfName={handleChangeOfName}
                              isSelected={isSelected}
                            />
                          </Button>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
              <InfiniteScroll
                callback={() => fetchNextPage()}
                isLoading={isFetching}
                disabled={!hasNextPage}
                scrollThreshold={0}
                scrollableTarget="ai-conversation-list-container"
              />
            </div>
            <div className={styles.searchDetailContainer}>
              <AISearchDetail conversation={selectedConversation} />
            </div>
          </CurrentStateWaitingConversationContext.Provider>
        </>
      )}
      {showCreateModal && (
        <CreateAISearchModal onClose={handleCloseCreateAISearchModal} />
      )}
    </div>
  );
};

export default AISearchContainer;
